// =============================================================================
// features/meu-whatsapp/useMeuWhatsApp.ts — Auto-atendimento de pareamento
// (F39-S08).
//
// Rotas `/me` (F39-S08): nenhuma recebe `agentId` ou `channelId`. O vendedor é
// derivado da sessão no backend — por isso os hooks daqui não têm parâmetro de
// identificação. Se algum dia aparecer um id de agente nesta camada, algo está
// errado no desenho.
//
// LGPD: `qr_base64` é CREDENCIAL DE PAREAMENTO — `gcTime: 0`, não vai para
// estado global, não é logado. `phone_e164` é PII do vendedor.
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { useToast } from '../../components/ui/Toast';
import { ApiError, api } from '../../lib/api';

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const MeuStatusSchema = z.enum(['STOPPED', 'STARTING', 'SCAN_QR_CODE', 'WORKING', 'FAILED']);

export type MeuStatus = z.infer<typeof MeuStatusSchema>;

export const SyncResultSchema = z.object({
  status: z.enum(['pending', 'running', 'completed', 'failed']),
  window_days: z.number().int(),
  chats_total: z.number().int(),
  chats_ignored: z.number().int(),
  contacts_in_window: z.number().int(),
  leads_created: z.number().int(),
  leads_linked: z.number().int(),
  messages_imported: z.number().int(),
  errors_count: z.number().int(),
  oldest_chat_at: z.string().nullable(),
  newest_chat_at: z.string().nullable(),
  finished_at: z.string().nullable(),
});

export type SyncResult = z.infer<typeof SyncResultSchema>;

const MeuPairingSchema = z.object({
  agent_id: z.string().uuid(),
  agent_name: z.string(),
  channel_id: z.string().uuid().nullable(),
  status: MeuStatusSchema,
  phone_e164: z.string().nullable(),
  sync: SyncResultSchema.nullable(),
});

const MeuQrSchema = z.object({
  qr_base64: z.string(),
  mime_type: z.string(),
  status: MeuStatusSchema,
});

export type MeuPairing = z.infer<typeof MeuPairingSchema>;
export type MeuQr = z.infer<typeof MeuQrSchema>;

export const MEU_STATUS_LABEL: Record<MeuStatus, string> = {
  STOPPED: 'Não conectado',
  STARTING: 'Iniciando…',
  SCAN_QR_CODE: 'Aguardando você escanear',
  WORKING: 'Conectado',
  FAILED: 'Falhou — tente de novo',
};

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const MEU_WHATSAPP_KEY = ['meu-whatsapp'] as const;
const meKey = [...MEU_WHATSAPP_KEY, 'me'] as const;
const qrKey = [...MEU_WHATSAPP_KEY, 'qr'] as const;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Meu estado de pareamento, com polling enquanto não houver desfecho.
 *
 * Para de pollar em WORKING e FAILED — depois do desfecho não há o que observar.
 */
export function useMeuPareamento(): ReturnType<typeof useQuery<MeuPairing>> {
  return useQuery<MeuPairing>({
    queryKey: meKey,
    queryFn: async () => MeuPairingSchema.parse(await api.get('/api/channels/waha/me')),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data === undefined) return false;

      // Importação em andamento: continua acompanhando até terminar, mesmo com
      // o pareamento já concluído — é o que faz o resultado aparecer sozinho.
      if (data.sync?.status === 'pending' || data.sync?.status === 'running') return 3000;

      if (data.status === 'WORKING' || data.status === 'FAILED') return false;
      if (data.status === 'STOPPED') return false; // ainda não começou
      return 3000;
    },
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: true,
  });
}

/** Meu QR. Só busca quando `enabled` — QR é credencial, não se pede por precaução. */
export function useMeuQr(enabled: boolean): ReturnType<typeof useQuery<MeuQr>> {
  return useQuery<MeuQr>({
    queryKey: qrKey,
    queryFn: async () => MeuQrSchema.parse(await api.get('/api/channels/waha/me/qr')),
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** Inicia o meu pareamento. Sem argumento: o backend sabe quem sou eu. */
export function useIniciarMeuPareamento(): ReturnType<typeof useMutation<MeuPairing, Error, void>> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<MeuPairing, Error, void>({
    mutationFn: async () =>
      MeuPairingSchema.parse(await api.post('/api/channels/waha/me/session', {})),
    onSuccess: (data) => {
      queryClient.setQueryData(meKey, data);
      void queryClient.invalidateQueries({ queryKey: qrKey });
    },
    onError: (err) => {
      toast(
        err instanceof ApiError && err.status === 503
          ? 'O serviço de WhatsApp está indisponível no momento. Tente mais tarde.'
          : 'Não foi possível iniciar a conexão.',
        'danger',
      );
    },
  });
}

/** Desconecta o meu aparelho. */
export function useDesconectarMeuAparelho(): ReturnType<typeof useMutation<void, Error, void>> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await api.delete('/api/channels/waha/me/session');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MEU_WHATSAPP_KEY });
      toast('Aparelho desconectado.', 'success');
    },
    onError: () => {
      toast('Não foi possível desconectar.', 'danger');
    },
  });
}
